import {
    Box,
    Button,
    FormControl,
    TextField,
    Theme,
    createStyles
} from "@material-ui/core";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    RouteComponentProps, withRouter,
} from "react-router-dom";

import CardComponent from "components/shared/CardComponent";
import { TranslationPrefixes } from "consts/translationPrefixes";
import { toast } from "react-toastify";
import { getSingleData, storeSet } from "services/service";

const styles = ({ palette }: Theme) => {
    return createStyles({
        container: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            margin: "30px auto 0",
            width: "65%"
        },
        formControl: {
            width: "100%",
            marginBottom: 15,
            "& > *": {
                width: "100%"
            }
        },
        button: {
            width: 250
        }
    });
};

export interface CredentialsData {
    api_key_name: string;
    api_key: string;
}

function CredentialsForm(props: RouteComponentProps & WithStyles<typeof styles>): JSX.Element {
    const { classes } = props;
    const { t } = useTranslation();
    const [data, setData] = useState<CredentialsData>({
        api_key_name: "",
        api_key: ""
    });
    const [isLoading, setIsLoading] = useState(true);

    const { credentialsForm } = TranslationPrefixes;

    const fetchStore = () => {
        getSingleData("amazonCredentials")
            .then((data) => {
                setIsLoading(false);
                if (data) {
                    const raw = data as unknown as Record<string, string | undefined>;
                    setData({
                        api_key_name: raw.api_key_name ?? "",
                        api_key: raw.api_key ?? ""
                    });
                }
            })
    }

    useEffect(() => {
        fetchStore();
    }, []);

    const updateCredentials = async () => {
        await storeSet("amazonCredentials", data);
        toast.success(data.api_key_name && data.api_key ? t(`${credentialsForm}updated`) : t(`${credentialsForm}added`));
        fetchStore();
    }

    const checkButtonDisable = () => {
        const areFilledFields = Object.values(data).every(Boolean);

        if (!areFilledFields) {
            return true;
        }

        return false;
    }

    interface FormInput {
        label: string;
        id: string;
        onChange: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
        value: string;
        multiline?: boolean;
        type?: "text" | "password";
    }

    const formControl = ({ label, id, multiline, type, onChange, value }: FormInput) => {
        return <Box className={classes.formControl}>
            <FormControl>
                <TextField
                    label={label}
                    onChange={(e) => onChange(e)}
                    id={id}
                    type={type}
                    aria-describedby="my-helper-text"
                    multiline={multiline}
                    value={value} />
            </FormControl>
        </Box>
    }

    const { api_key_name, api_key } = data;

    const inputs: FormInput[] = [
        {
            label: t(`${credentialsForm}apiKeyName`),
            id: "apiKeyName",
            value: api_key_name,
            onChange: ({ currentTarget }) => {
                setData({
                    ...data,
                    api_key_name: currentTarget.value
                })
            }
        },
        {
            label: t(`${credentialsForm}apiKey`),
            id: "apiKey",
            value: api_key,
            type: "password",
            onChange: ({ currentTarget }) => {
                setData({
                    ...data,
                    api_key: currentTarget.value
                })
            }
        },
    ];

    return (
        <CardComponent title={t(`${credentialsForm}title`)} isLoading={isLoading}>
            <Box className={classes.container}>
                {inputs.map(formControl)}
                <Button
                    color="primary"
                    variant="contained"
                    className={classes.button}
                    disabled={checkButtonDisable()}
                    onClick={updateCredentials}
                >
                    {data.api_key_name && data.api_key ? t(`${credentialsForm}update`) : t(`${credentialsForm}add`)}
                </Button>
            </Box>
        </CardComponent>
    );

}

export default withRouter(withStyles(styles)(CredentialsForm));
